import React, { useState, KeyboardEvent, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  ArrowRight,
  X,
  Copy,
  Clipboard,
  ChevronUp,
  ChevronDown,
  Trash2,
  ArrowUp01,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { characterShapes } from "@/config/font";
import { Checkbox } from "@/components/ui/checkbox";
import { v4 as uuidv4 } from "uuid";
import { HiVis } from "@/types/editor/blocks";
import { updateHiVisDesign } from "@/lib/hivis-manager";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type Range = {
  id: string;
  start: number;
  end: number;
  quantity: number;
  padding: number;
  originalInput: string;
};

const QuantityControl = ({
  quantity,
  onChange,
}: {
  quantity: number;
  onChange: (change: number) => void;
}) => (
  <div className='flex items-center gap-1 border rounded-md w-24'>
    <Button
      type='button'
      variant='ghost'
      size='icon'
      className='h-8 w-8'
      onClick={() => onChange(-1)}
    >
      <ChevronDown className='h-4 w-4' />
    </Button>
    <span className='w-8 text-center'>{quantity}</span>
    <Button
      type='button'
      variant='ghost'
      size='icon'
      className='h-8 w-8'
      onClick={() => onChange(1)}
    >
      <ChevronUp className='h-4 w-4' />
    </Button>
  </div>
);

interface CustomInputProps {
  hiVises: HiVis[];
  onHiVisChange: (hiVis: HiVis) => void;
  selectedHiVisIndex?: number;
  range?: boolean;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  isVertical?: boolean;
}

export default function CustomInput({
  hiVises,
  onHiVisChange,
  selectedHiVisIndex,
  range = false,
  inputRef,
  isVertical = false,
}: CustomInputProps) {
  const { toast } = useToast();
  const [newRangeInput, setNewRangeInput] = useState("");
  const [newRangeQuantity, setNewRangeQuantity] = useState(1);
  const [ranges, setRanges] = useState<Range[]>([]);
  const [selectedRange, setSelectedRange] = useState<Range | null>(null);
  const [currentNumber, setCurrentNumber] = useState<number | null>(null);
  const [useRanges, setUseRanges] = useState(false);

  // Get the currently selected HiVis
  const selectedHiVis =
    selectedHiVisIndex !== undefined ? hiVises[selectedHiVisIndex] : hiVises[0]; // Default to first HiVis if no index provided
  const text = selectedHiVis?.assetNumber || "";
  const isStacked = selectedHiVis?.stackFirstTwo || false;

  // Check if stacking button should be shown
  const shouldShowStackingButton = text.length > 2 && !isVertical;

  // Update the hiVises array with new values
  const updateHiVis = useCallback(
    (updates: Partial<HiVis>) => {
      const indexToUpdate =
        selectedHiVisIndex !== undefined ? selectedHiVisIndex : 0;

      const updatedHiVises = [...hiVises];
      updatedHiVises[indexToUpdate] = {
        ...updatedHiVises[indexToUpdate],
        ...updates,
      };

      // Use the shared logic to update the design if assetNumber changes
      if (
        updates.assetNumber !== undefined ||
        updates.stackFirstTwo !== undefined ||
        updates.naturalWidth !== undefined
      ) {
        updatedHiVises[indexToUpdate] = updateHiVisDesign(
          updatedHiVises[indexToUpdate]
        );
      }

      onHiVisChange(updatedHiVises[indexToUpdate]);
    },
    [hiVises, selectedHiVisIndex, onHiVisChange]
  );

  const showNotification = (message: string, type: "success" | "error") => {
    toast({
      description: message,
      variant: type === "error" ? "destructive" : "default",
    });
  };

  const parseRange = (
    value: string
  ): {
    start: number;
    end: number;
    padding: number;
    originalInput: string;
  } | null => {
    if (value.includes("-")) {
      const [startStr, endStr] = value.split("-");
      const start = parseInt(startStr);
      const end = parseInt(endStr);
      const padding = startStr.length;

      if (!isNaN(start) && !isNaN(end) && start < end) {
        return {
          start,
          end,
          padding,
          originalInput: `${start.toString().padStart(padding, "0")}-${end
            .toString()
            .padStart(padding, "0")}`,
        };
      }
    } else {
      const number = parseInt(value);
      if (!isNaN(number)) {
        const padding = value.length;
        return {
          start: number,
          end: number,
          padding,
          originalInput: number.toString().padStart(padding, "0"),
        };
      }
    }
    return null;
  };

  const checkOverlap = (
    newRange: { start: number; end: number; padding: number },
    excludeId?: string
  ): boolean => {
    return ranges.some(
      (existing) =>
        existing.id !== excludeId &&
        existing.padding === newRange.padding &&
        ((newRange.start >= existing.start && newRange.start <= existing.end) ||
          (newRange.end >= existing.start && newRange.end <= existing.end) ||
          (newRange.start <= existing.start && newRange.end >= existing.end))
    );
  };

  const handleRangeChange = (id: string, newValue: string) => {
    const parsed = parseRange(newValue);
    if (parsed) {
      setRanges((prev: Range[]) => {
        const updated = prev.map((range: Range) => {
          if (range.id === id) {
            const updatedRange = {
              ...range,
              ...parsed,
              originalInput: newValue,
            };
            if (selectedRange?.id === id && currentNumber !== null) {
              if (currentNumber < parsed.start || currentNumber > parsed.end) {
                setCurrentNumber(parsed.start);
              }
            }
            return updatedRange;
          }
          return range;
        });
        return updated;
      });
    }
  };

  const addNewRange = () => {
    const parsed = parseRange(newRangeInput.trim());
    if (parsed) {
      if (checkOverlap(parsed)) {
        showNotification(
          `Range overlaps with existing ${parsed.padding}-digit range`,
          "error"
        );
        return;
      }

      const newRange = {
        id: uuidv4(),
        start: parsed.start,
        end: parsed.end,
        padding: parsed.padding,
        originalInput: parsed.originalInput,
        quantity: newRangeQuantity,
      };
      setRanges((prev) => [...prev, newRange]);
      setNewRangeInput("");
      setNewRangeQuantity(1);
      setSelectedRange(newRange);
      setCurrentNumber(newRange.start);
    } else {
      showNotification(
        "Invalid range format. Use format like '01' or '01-05'",
        "error"
      );
    }
  };

  const handleNewRangeChange = (value: string) => {
    setNewRangeInput(value);

    if (value.endsWith(",")) {
      const baseValue = value.slice(0, -1).trim();
      const parsed = parseRange(baseValue);
      if (parsed) {
        if (checkOverlap(parsed)) {
          showNotification(
            `Range overlaps with existing ${parsed.padding}-digit range`,
            "error"
          );
          return;
        }

        const newRange = {
          id: uuidv4(),
          start: parsed.start,
          end: parsed.end,
          padding: parsed.padding,
          originalInput: parsed.originalInput,
          quantity: newRangeQuantity,
        };
        setRanges((prev) => [...prev, newRange]);
        setNewRangeInput("");
        setNewRangeQuantity(1);
        setSelectedRange(newRange);
      } else {
        showNotification(
          "Invalid range format. Use format like '01' or '01-05'",
          "error"
        );
      }
    }
  };

  const handleNewRangeKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && newRangeInput.trim()) {
      e.preventDefault();
      addNewRange();
    }
  };

  const updateRangeQuantity = (id: string, change: number) => {
    setRanges((current) =>
      current.map((range) => {
        if (range.id === id) {
          const updatedRange = {
            ...range,
            quantity: Math.max(1, range.quantity + change),
          };
          if (selectedRange?.id === id) {
            setSelectedRange(updatedRange);
          }
          return updatedRange;
        }
        return range;
      })
    );
  };

  const handleRangeClick = (range: Range) => {
    setSelectedRange(range);
    setCurrentNumber(range.start);
  };

  const handleDelete = (range: Range) => {
    setRanges((current) => {
      const newRanges = current.filter((r) => r.id !== range.id);

      if (selectedRange?.id === range.id) {
        if (newRanges.length > 0) {
          setSelectedRange(newRanges[0]);
          setCurrentNumber(newRanges[0].start);
        } else {
          setSelectedRange(null);
          setCurrentNumber(null);
        }
      }

      return newRanges;
    });
  };

  const handlePrevRange = () => {
    if (!selectedRange) return;

    if (currentNumber !== null) {
      if (currentNumber === selectedRange.start) {
        const currentIndex = ranges.findIndex((r) => r.id === selectedRange.id);
        const prevIndex =
          currentIndex > 0 ? currentIndex - 1 : ranges.length - 1;
        const prevRange = ranges[prevIndex];
        setSelectedRange(prevRange);
        setCurrentNumber(prevRange.end);
      } else {
        setCurrentNumber(currentNumber - 1);
      }
    } else {
      setCurrentNumber(selectedRange.end);
    }
  };

  const handleNextRange = () => {
    if (!selectedRange) return;

    if (currentNumber !== null) {
      if (currentNumber === selectedRange.end) {
        const currentIndex = ranges.findIndex((r) => r.id === selectedRange.id);
        const nextIndex =
          currentIndex < ranges.length - 1 ? currentIndex + 1 : 0;
        const nextRange = ranges[nextIndex];
        setSelectedRange(nextRange);
        setCurrentNumber(nextRange.start);
      } else {
        setCurrentNumber(currentNumber + 1);
      }
    } else {
      setCurrentNumber(selectedRange.start);
    }
  };

  const handleCopy = async () => {
    if (useRanges) {
      if (!ranges.length) {
        showNotification("Add some ranges first", "error");
        return;
      }

      const formattedEntries = ranges.flatMap((range) => {
        const rangeNumbers = [];
        for (let i = 0; i < range.quantity; i++) {
          for (let num = range.start; num <= range.end; num++) {
            rangeNumbers.push(
              `${text}${num.toString().padStart(range.padding, "0")}`
            );
          }
        }
        return rangeNumbers;
      });

      const formattedText = formattedEntries.join("\n");

      try {
        await navigator.clipboard.writeText(formattedText);
        showNotification("Values copied to clipboard", "success");
      } catch {
        showNotification("Failed to copy to clipboard", "error");
      }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        showNotification("Text copied to clipboard", "success");
      } catch {
        showNotification("Failed to copy to clipboard", "error");
      }
    }
  };

  const handlePaste = async () => {
    try {
      const clipText = await navigator.clipboard.readText();
      const items = clipText.split(/[\n\s,]+/).filter(Boolean);

      if (items.length === 0) {
        showNotification("No valid items found", "error");
        return;
      }

      const firstItem = items[0];
      const prefixMatch = firstItem.match(/^([A-Za-z-]+)/);
      if (!prefixMatch) {
        showNotification("No valid prefix found", "error");
        return;
      }

      const prefix = prefixMatch[0];
      const numbers: { value: number; originalStr: string }[] = [];

      for (const item of items) {
        if (!item.startsWith(prefix)) {
          showNotification("Inconsistent prefix in items", "error");
          return;
        }

        const numberStr = item.slice(prefix.length);
        const number = parseInt(numberStr);
        if (!isNaN(number)) {
          numbers.push({
            value: number,
            originalStr: numberStr,
          });
        }
      }

      if (numbers.length === 0) {
        showNotification("No valid numbers found", "error");
        return;
      }

      // Update the asset number in the selected HiVis
      const upperPrefix = prefix.toUpperCase();
      updateHiVis({ assetNumber: upperPrefix });

      setUseRanges(true);
      const sortedNumbers = Array.from(new Set(numbers.map((n) => n.value)))
        .sort((a, b) => a - b)
        .map((n) => ({
          value: n,
          originalStr: numbers.find((num) => num.value === n)!.originalStr,
        }));

      const newRanges: Range[] = [];
      let rangeStart = sortedNumbers[0].value;
      let prev = sortedNumbers[0].value;
      let padding = sortedNumbers[0].originalStr.length;

      for (let i = 1; i <= sortedNumbers.length; i++) {
        const current = sortedNumbers[i]?.value;
        if (current !== prev + 1 || i === sortedNumbers.length) {
          const originalInput =
            rangeStart === prev
              ? rangeStart.toString().padStart(padding, "0")
              : `${rangeStart.toString().padStart(padding, "0")}-${prev
                  .toString()
                  .padStart(padding, "0")}`;

          const newRange = {
            id: uuidv4(),
            start: rangeStart,
            end: prev,
            padding,
            originalInput,
            quantity: 1,
          };
          newRanges.push(newRange);
          if (newRanges.length === 1) {
            setSelectedRange(newRange);
            setCurrentNumber(newRange.start);
          }
          if (i < sortedNumbers.length) {
            rangeStart = current;
            padding = sortedNumbers[i].originalStr.length;
          }
        }
        prev = current;
      }

      setRanges(newRanges);
      showNotification("Values pasted successfully", "success");
    } catch {
      showNotification("Failed to paste from clipboard", "error");
    }
  };

  const handleClearAll = () => {
    setRanges([]);
    setSelectedRange(null);
    setCurrentNumber(null);
    showNotification("All ranges cleared", "success");
  };

  const showNavigationButtons = selectedRange !== null && ranges.length > 0;

  const headerText = useRanges
    ? !selectedRange
      ? text
      : currentNumber !== null
        ? `${text}${currentNumber
            .toString()
            .padStart(selectedRange.padding, "0")}`
        : selectedRange.originalInput.includes("-")
          ? selectedRange.originalInput
          : `${text}${selectedRange.originalInput}`
    : text;

  // Update the text in the HiVis when headerText changes
  useEffect(() => {
    if (selectedHiVis && headerText !== selectedHiVis.assetNumber) {
      updateHiVis({ assetNumber: headerText });
    }
  }, [headerText, selectedHiVis, updateHiVis]);

  // Initialize state from the selected HiVis
  useEffect(() => {
    if (selectedHiVis) {
      setUseRanges(false);
      setRanges([]);
      setSelectedRange(null);
      setCurrentNumber(null);
    }
  }, [selectedHiVisIndex, selectedHiVis]);

  return (
    <div className='space-y-4'>
      <div className='hidden md:flex flex-col'>
        <div className='flex justify-between items-start'>
          <div className='flex flex-col'>
            <h2 className='text-4xl font-semibold'>{headerText}</h2>
            {isStacked && (
              <span className='text-sm text-primary italic'>
                First two characters stacked
              </span>
            )}
          </div>
          {range && (
            <Button
              variant='outline'
              size='sm'
              className='flex gap-2'
              onClick={handlePaste}
            >
              <Clipboard className='h-4 w-4' />
              Paste
            </Button>
          )}
        </div>

        {showNavigationButtons && useRanges && (
          <div className='flex gap-2 mt-2'>
            <Button
              variant='outline'
              size='sm'
              className='h-8 w-8 p-0'
              onClick={handlePrevRange}
            >
              <ArrowLeft className='h-4 w-4' />
            </Button>
            <Button
              variant='outline'
              size='sm'
              className='h-8 w-8 p-0'
              onClick={handleNextRange}
            >
              <ArrowRight className='h-4 w-4' />
            </Button>
          </div>
        )}
      </div>

      <div className='space-y-2 w-full'>
        <Label htmlFor='text-input'>
          {useRanges ? "Prefix" : "Decal Number"}
          {selectedHiVis?.minChars !== undefined &&
            selectedHiVis?.maxChars !== undefined && (
              <span className='text-muted-foreground ml-1 text-sm'>
                {selectedHiVis.minChars === selectedHiVis.maxChars
                  ? `(${selectedHiVis.minChars} characters)`
                  : `(${selectedHiVis.minChars}-${selectedHiVis.maxChars} characters)`}
              </span>
            )}
        </Label>
        <div className='flex gap-2 items-center w-full'>
          <div className='w-full flex-1'>
            <Input
              id='text-input'
              type='text'
              value={text}
              ref={inputRef}
              onChange={(e) => {
                const newValue = e.target.value.toLowerCase();

                // Check if current text is just a placeholder space and new value has characters
                const isCurrentlyPlaceholder = text.trim() === "";

                // Improved validation to handle all types of whitespace
                const validValue = newValue
                  .split("")
                  .filter(
                    (char) =>
                      // Handle all types of whitespace
                      /\s/.test(char) ||
                      char === "-" ||
                      char === "." ||
                      char === "_" ||
                      char in characterShapes
                  )
                  .join("");

                // Remove leading space if we're coming from the placeholder state
                const processedValue = isCurrentlyPlaceholder
                  ? validValue.replace(/^\s+/, "")
                  : validValue;

                const upperValue = processedValue.toUpperCase();

                // Only validate maxChars if it's set
                if (
                  selectedHiVis?.maxChars !== undefined &&
                  upperValue.length > selectedHiVis.maxChars
                ) {
                  showNotification(
                    `Maximum ${selectedHiVis.maxChars} characters allowed`,
                    "error"
                  );
                  return;
                }

                // Ensure asset number is never completely empty
                // If emptied, use a placeholder space that won't display visibly
                const finalValue = upperValue.length === 0 ? " " : upperValue;

                // Ensure no trailing spaces if we have actual characters
                const cleanValue = finalValue.trim() === "" ? " " : finalValue;

                // Update the hiVis asset number
                updateHiVis({ assetNumber: cleanValue });
              }}
              onBlur={() => {
                // Only validate minChars if it's set
                if (
                  selectedHiVis?.minChars !== undefined &&
                  text.length < selectedHiVis.minChars
                ) {
                  showNotification(
                    `Minimum ${selectedHiVis.minChars} characters required`,
                    "error"
                  );
                }

                // Ensure asset number is never empty on blur
                // But also ensure we're not just filling it with spaces when there's already content
                const trimmedText = text.trim();
                if (trimmedText.length === 0) {
                  updateHiVis({ assetNumber: " " });
                } else if (trimmedText !== text) {
                  // If there are trailing or leading spaces but actual content exists, trim it
                  updateHiVis({ assetNumber: trimmedText });
                }
              }}
              className='w-full'
              placeholder={
                useRanges ? "Enter prefix..." : "Enter decal number..."
              }
              autoComplete='off'
              tabIndex={1}
              autoFocus={false}
            />
          </div>

          {shouldShowStackingButton && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type='button'
                    variant={
                      selectedHiVis?.stackFirstTwo ? "default" : "outline"
                    }
                    size='icon'
                    onClick={() =>
                      updateHiVis({
                        stackFirstTwo: !selectedHiVis?.stackFirstTwo,
                      })
                    }
                    className={cn(
                      "h-10 w-10 flex-shrink-0",
                      selectedHiVis?.stackFirstTwo && "text-primary-foreground"
                    )}
                  >
                    <ArrowUp01 className='h-5 w-5' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Stack first two characters (adds _S suffix)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {range && (
        <>
          <div className='flex items-center space-x-2'>
            <Checkbox
              id='useRanges'
              checked={useRanges}
              onCheckedChange={(checked) => setUseRanges(checked as boolean)}
            />
            <Label htmlFor='useRanges'>Have a range of asset numbers?</Label>
          </div>

          <div className='flex items-center space-x-2'>
            <Checkbox
              id='naturalWidth'
              checked={selectedHiVis?.naturalWidth || false}
              onCheckedChange={(checked) =>
                updateHiVis({ naturalWidth: checked as boolean })
              }
            />
            <Label htmlFor='naturalWidth'>Use Natural Width</Label>
          </div>

          {useRanges && (
            <div className='space-y-2 w-full'>
              <div className='flex justify-between items-center'>
                <Label>Range</Label>
                <div className='flex gap-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    className='flex gap-2'
                    onClick={handleCopy}
                  >
                    <Copy className='h-4 w-4' />
                    Copy
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    className='flex gap-2'
                    onClick={handleClearAll}
                  >
                    <Trash2 className='h-4 w-4' />
                    Clear
                  </Button>
                </div>
              </div>

              <div className='space-y-2 mt-4'>
                {ranges.map((range) => (
                  <div
                    key={range.id}
                    className={`flex items-center gap-2 p-2 rounded-md cursor-pointer ${
                      selectedRange?.id === range.id
                        ? "border border-primary"
                        : ""
                    }`}
                    onClick={() => handleRangeClick(range)}
                  >
                    <div className='flex-1'>
                      <Input
                        value={range.originalInput}
                        onChange={(e) =>
                          handleRangeChange(range.id, e.target.value)
                        }
                        className='w-full'
                        placeholder='Enter a number or range (e.g., 01 or 01-05)'
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <QuantityControl
                      quantity={range.quantity}
                      onChange={(change) =>
                        updateRangeQuantity(range.id, change)
                      }
                    />
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      className='h-8 w-8'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(range);
                      }}
                    >
                      <X className='h-4 w-4' />
                    </Button>
                  </div>
                ))}
              </div>

              <div className='flex w-full items-center gap-2 p-2 rounded-md'>
                <div className='flex-1'>
                  <Input
                    value={newRangeInput}
                    onChange={(e) => handleNewRangeChange(e.target.value)}
                    onKeyDown={handleNewRangeKeyDown}
                    className='w-full'
                    placeholder='(e.g. 5 or 1-5)'
                  />
                </div>
                <QuantityControl
                  quantity={newRangeQuantity}
                  onChange={(change) =>
                    setNewRangeQuantity(Math.max(1, newRangeQuantity + change))
                  }
                />
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8'
                  onClick={addNewRange}
                >
                  <X className='h-4 w-4 rotate-45' />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
